import axios from 'axios';

// BaseUrl always ends with "/" so every call can just do:
//   axios.post(BaseUrl + "admin/users/", { ... })
export const BaseUrl = (process.env.REACT_APP_API_URL || 'https://code.mannaplag.mannaanalytics.com/api') + '/';

/**
 * Admin sessions are kept completely separate from the normal user
 * session (see services/api.ts) so that being logged in as an admin
 * in one tab never overwrites / logs out a regular user session.
 */
export const AdminTokenStorage = {
  getAccess: () => localStorage.getItem('admin_access_token'),
  getRefresh: () => localStorage.getItem('admin_refresh_token'),
  setTokens: (access: string, refresh: string) => {
    localStorage.setItem('admin_access_token', access);
    localStorage.setItem('admin_refresh_token', refresh);
  },
  clear: () => {
    localStorage.removeItem('admin_access_token');
    localStorage.removeItem('admin_refresh_token');
    localStorage.removeItem('admin_user');
  },
  setUser: (user: object) => localStorage.setItem('admin_user', JSON.stringify(user)),
  getUser: () => {
    const u = localStorage.getItem('admin_user');
    return u ? JSON.parse(u) : null;
  },
};

let isRefreshing = false;
let refreshQueue: Array<(token: string | null) => void> = [];

async function attemptAdminTokenRefresh(): Promise<string | null> {
  const refresh = AdminTokenStorage.getRefresh();
  if (!refresh) return null;

  if (isRefreshing) {
    return new Promise((resolve) => refreshQueue.push(resolve));
  }

  isRefreshing = true;
  try {
    const res = await axios.post(BaseUrl + 'auth/token/refresh/', { refresh });
    const newAccess = res.data.access;
    const newRefresh = res.data.refresh || refresh;
    AdminTokenStorage.setTokens(newAccess, newRefresh);
    refreshQueue.forEach(cb => cb(newAccess));
    refreshQueue = [];
    return newAccess;
  } catch {
    AdminTokenStorage.clear();
    refreshQueue.forEach(cb => cb(null));
    refreshQueue = [];
    return null;
  } finally {
    isRefreshing = false;
  }
}

/**
 * These two interceptors are attached to the global `axios` object (not a
 * separate instance) so that every admin page can just call
 * `axios.post(BaseUrl + "admin/users/", data)` directly, exactly like the
 * examples below, and still get the admin Bearer token attached + automatic
 * 401 refresh-and-retry — without passing headers manually each time.
 *
 * They only touch requests whose url starts with BaseUrl, so normal-user
 * axios calls elsewhere in the app (if any are added later) are untouched.
 */
// Endpoints that must NEVER get an Authorization header attached, even if a
// (possibly stale/expired) admin token happens to be sitting in localStorage.
// Sending a bad Bearer token on a *login* request is a classic bug: many
// backends run their JWT auth check globally, before the view logic, so an
// expired token on the login call itself gets the whole request rejected —
// the user sees "invalid credentials" even though their email/password were
// correct. Same idea for token-refresh (it takes the refresh token in the
// body, not a Bearer header) and logout.
const AUTH_EXEMPT_PATHS = ['admin/login/', 'auth/token/refresh/', 'auth/logout/'];
function isAuthExempt(url?: string): boolean {
  if (!url) return false;
  return AUTH_EXEMPT_PATHS.some(p => url.endsWith(p));
}

axios.interceptors.request.use((config) => {
  if (config.url && config.url.startsWith(BaseUrl) && !isAuthExempt(config.url)) {
    const token = AdminTokenStorage.getAccess();
    if (token) {
      config.headers = config.headers || {};
      (config.headers as any).Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

axios.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config as any;
    const isAdminUrl = typeof original?.url === 'string' && original.url.startsWith(BaseUrl);
    const exempt = isAuthExempt(original?.url);
    const token = AdminTokenStorage.getAccess();

    if (isAdminUrl && !exempt && error.response?.status === 401 && !original._retry && token) {
      original._retry = true;
      const newToken = await attemptAdminTokenRefresh();
      if (newToken) {
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return axios(original);
      }
    }
    return Promise.reject(error);
  }
);

function extractErrorMessage(err: any, fallback: string): string {
  const data = err?.response?.data || {};
  const values = Object.values(data) as any[];
  return (
    data?.error || data?.detail ||
    (Array.isArray(values[0]) ? values[0][0] : values[0]) ||
    fallback
  );
}

/**
 * Small helper: try a real backend endpoint, and if it isn't available yet
 * (404 / network error / not implemented), fall back to local sample data
 * so the admin panel is always usable while the backend admin API is
 * being built out. Once the real endpoint exists this transparently
 * starts using it — no UI changes required.
 *
 * Usage: withFallback(axios.get(BaseUrl + 'admin/users/'), FALLBACK_USERS)
 */
export async function withFallback<T>(request: Promise<{ data: T }>, fallback: T): Promise<T> {
  try {
    const res = await request;
    return (res.data ?? fallback) as T;
  } catch {
    return fallback;
  }
}

export const AdminAuthAPI = {
  // Admin accounts authenticate against a dedicated endpoint that returns a
  // flat payload: { access, refresh, is_staff, email } — not the
  // { user, tokens } shape used by the normal-user /auth/login/ endpoint.
  login: async (email: string, password: string) => {
    try {
      const res = await axios.post(BaseUrl + 'admin/login/', { email, password });
      return res.data;
    } catch (err: any) {
      throw new Error(extractErrorMessage(err, 'Invalid credentials.'));
    }
  },
  logout: (refresh: string) =>
    axios.post(BaseUrl + 'auth/logout/', { refresh }).catch(() => { }),
  me: () => axios.get(BaseUrl + 'auth/me/').then(res => res.data),
};
